<template>
  <main-layout
    class="pro-rights-detail"
    :page-title="pageTitle"
    :hide-header="hideHeader"
    custom-back
    @listen-back="goBack"
  >
    <div slot="main-content" class="rd-main" @click="onMainClick">
      <!-- 顶部深绿渐变背景带：设计稿为「底部带弧线」的矢量（PC 13776:833 / H5 13664:1146）。
         用 <img> + object-fit: fill 让图**严格**铺满元素盒（宽高都不留空），
         高度由 CSS 变量推导（见 .rd-hero-bg 样式注释） -->
      <img class="rd-hero-bg" :src="heroBand" alt="" />

      <div class="rd-container">
        <!-- 首屏玻璃卡：标题 + 副标题 + 主视觉 -->
        <section class="rd-hero">
          <!-- 卡片左下的大号 V 徽章水印（禅道 #23490「缺少背景图标」）：
               设计稿节点 2374:297，纯白 5% 不透明度。用 <img> 而非 background
               是为了让 SVG 保持矢量清晰；绝对定位在 .rd-hero 内、z-index 低于文字 -->
          <img class="rd-hero-mark" :src="hero.mark" alt="" />
          <div class="rd-hero-text">
            <!-- 标题用设计稿切图：文案为「专业版9大权益升级」，字体是 Alibaba PuHuiTi 2.0
                 （项目未内置该字体，CSS 用系统字体回退后字形差异明显）。
                 切图保留渐变填充 + 文字阴影，两端各一张（见 RIGHTS_DETAIL_HERO.titleImage）。 -->
            <img class="rd-hero-title" :src="heroTitle" :alt="hero.title" />
            <!-- 副标题：PC 设计稿为单行、H5 为两行，两端文案一致只是断行不同，
                 故两套都渲染、由媒体查询控制显隐（避免用 JS 判断断点造成跳变） -->
            <p class="rd-hero-sub rd-hero-sub--pc">{{ hero.subtitle }}</p>
            <p class="rd-hero-sub rd-hero-sub--h5">
              <span
                v-for="(line, li) in heroLines"
                :key="li"
                class="rd-hero-sub-line"
                >{{ line }}</span
              >
            </p>
          </div>
          <img class="rd-hero-visual" :src="hero.visual" alt="" />
        </section>

        <!-- "9 项专业版核心权益解释" 标题条 -->
        <div class="rd-strip">
          <img class="rd-strip-deco" :src="strip.deco" alt="" />
          <span class="rd-strip-text">
            <b class="rd-strip-num">{{ strip.number }}</b>
            <span class="rd-strip-desc">{{ strip.text }}</span>
          </span>
          <img class="rd-strip-deco" :src="strip.deco" alt="" />
        </div>

        <!-- 9 大权益卡片 -->
        <div class="rd-benefits">
          <article
            v-for="item in benefits"
            :key="item.no"
            class="rd-card"
            :class="{ 'rd-card--half': item.no <= 2 }"
            :style="cardCornerStyle"
          >
            <h2 class="rd-card-title">{{ item.no }}、{{ item.title }}</h2>

            <div class="rd-card-body">
              <template v-for="(block, bi) in item.blocks">
                <!-- 段落 -->
                <p v-if="block.type === 'para'" :key="'p' + bi" class="rd-para">
                  <span
                    v-for="(seg, si) in block.segments"
                    :key="si"
                    :class="{ 'is-strong': seg.strong }"
                    >{{ seg.text }}</span
                  >
                </p>
                <!-- 列表 -->
                <ul v-else :key="'l' + bi" class="rd-list">
                  <li v-for="(row, ri) in block.items" :key="ri">
                    <span
                      v-for="(seg, si) in row.segments"
                      :key="si"
                      :class="{
                        'is-strong': seg.strong,
                        'is-label': seg.label,
                      }"
                      >{{ seg.text }}</span
                    >
                  </li>
                </ul>
              </template>
            </div>

            <!-- 配图区：按 layout 渲染 -->
            <div class="rd-media" :class="'rd-media--' + mediaLayout(item)">
              <!-- 手机截图（卡 1 / 卡 2）：整张切图，橙色说明已烧进图内 -->
              <template v-if="mediaLayout(item) === 'phone'">
                <div class="rd-phone">
                  <img :src="item.media.items[0].src" alt="" />
                </div>
              </template>

              <!-- 4 张一组的并排（卡 4 / 卡 5）——禅道 #23492：
                   设计稿里卡 4 是**一组 4 张**（827 居中）；
                   卡 5 是**两组并排**（各 4 张、组间 30px、合计 984），两组横向排列而非上下两行。
                   故用外层 .rd-row4-wrap 承载「组」，由组数决定限宽。 -->
              <template v-else-if="mediaLayout(item) === 'row4'">
                <div
                  class="rd-row4-wrap"
                  :class="{ 'is-multi': chunk(item.media.items, 4).length > 1 }"
                >
                  <div
                    v-for="(row, ri) in chunk(item.media.items, 4)"
                    :key="ri"
                    class="rd-row4"
                  >
                    <img v-for="(m, mi) in row" :key="mi" :src="m.src" alt="" />
                  </div>
                </div>
              </template>

              <!-- 3 张并排（卡 3）：3 张独立切图，圆角由 .rd-row3 容器统一控制 -->
              <template v-else-if="mediaLayout(item) === 'row3'">
                <div class="rd-row3">
                  <div
                    v-for="(m, mi) in item.media.items"
                    :key="mi"
                    class="rd-row3-item"
                  >
                    <img :src="m.src" alt="" />
                  </div>
                </div>
              </template>

              <!-- 两张并排（卡 9）：整张切图 -->
              <template v-else-if="mediaLayout(item) === 'duo'">
                <div
                  v-for="(m, mi) in item.media.items"
                  :key="mi"
                  class="rd-duo-item"
                >
                  <img :src="m.src" alt="" />
                </div>
              </template>

              <!-- 宽图（卡 6）：整张切图（红色标注已烧进图内），圆角由 .rd-wide 容器控制 -->
              <template v-else-if="mediaLayout(item) === 'wide'">
                <div class="rd-wide">
                  <img :src="item.media.pc.src" alt="" />
                </div>
              </template>

              <!-- 俯瞰图（卡 8）——两端版式不同（禅道 #23492）：
                   PC 设计稿为**一行两图**（8-1 导出弹窗对比 + 8-2 俯瞰图对比），
                   H5 设计稿为**上下两行**。由 .rd-maprow 的断点控制。
                   两张都是**整组切图**：红色说明文字（8-1）与「专业版/免费版」角标（8-2）
                   都随整组导出烧进图内，页面不做 DOM 文字叠加。 -->
              <template v-else-if="mediaLayout(item) === 'mapPair'">
                <div class="rd-maprow">
                  <div class="rd-dialogs">
                    <img :src="item.media.dialogs.src" alt="" />
                  </div>
                  <div class="rd-dialog">
                    <img :src="item.media.mapCompare.src" alt="" />
                  </div>
                </div>
              </template>
            </div>
          </article>
        </div>
      </div>

      <!-- 点击配图查看大图（禅道 #23490），同卡片内可左右连着看。
           ⚠️ 必须放在 main-content slot **内部**：MainLayout 只渲染该 slot，
           放在 slot 外的兄弟节点不会被渲染（表现为按时 viewerVisible=true 但预览不出现）。 -->
      <rights-image-viewer
        :visible.sync="viewerVisible"
        :list="viewerList"
        :index.sync="viewerIndex"
      />
    </div>
  </main-layout>
</template>

<script>
import MainLayout from "@/layout/MainLayout";
import RightsImageViewer from "./components/RightsImageViewer.vue";
import { PRO_PLAN_TEXT } from "@/consts/proPlan";
import {
  RIGHTS_DETAIL_HERO,
  RIGHTS_DETAIL_STRIP,
  RIGHTS_BENEFITS,
  RIGHTS_DETAIL_BAND,
  RIGHTS_DETAIL_CORNER,
  RIGHTS_DETAIL_TITLE_IMG,
} from "@/consts/proPlanRightsDetail";
import { isMobile as isMobileUtil, onApiReady } from "@/utils";

const BAND = RIGHTS_DETAIL_BAND;
const CORNER = RIGHTS_DETAIL_CORNER;
const TITLE_IMG = RIGHTS_DETAIL_TITLE_IMG;

/**
 * 专业版权益详情页（PC 与 H5 同一页面实现）
 *
 * 两端文案完全一致，差异仅在版式与配图（设计稿：
 *  H5 13664-1128 / PC 13776-804），由 CSS 媒体查询按断点切换：
 *  - PC：内容区 1098 居中；卡片 1、2 并排；标题 24/正文 16
 *  - H5：内容区占满（左右 15~16）；卡片纵向堆叠；标题 16/正文 14
 * 仅「卡 6 配图」「卡 3 配图组」这类两端素材不同的地方才走 JS 判断。
 * 文案与图片全部集中在 consts/proPlanRightsDetail.js。
 *
 * H5 特例：本页在 H5 下允许免登录访问且隐藏头部导航（见 permission.js 的
 * PUBLIC_MOBILE_PATHS 与下方 hideHeader），因为它是可对外分享的说明页。
 */
export default {
  name: "ProPlanRightsDetail",
  components: { MainLayout, RightsImageViewer },
  data() {
    return {
      pageTitle: `${PRO_PLAN_TEXT.proPrefix}权益详情`,
      hero: RIGHTS_DETAIL_HERO,
      strip: RIGHTS_DETAIL_STRIP,
      benefits: RIGHTS_BENEFITS,
      // 用于「两端素材/版式不同」的判断（卡 3 配图组、卡 6 配图、隐藏头部）
      isMobile: isMobileUtil(),
      // resize 节流定时器（见 handleResize）
      resizeTimer: null,
      // APICloud 就绪后的兜底校正定时器（见 mounted）
      apiFallbackTimer: null,
      // 点击查看大图（禅道 #23490）：只作用于卡片内的配图（.rd-media 下）。
      // viewerList 为**同卡片内**的全部图片（含装饰角标），viewerIndex 指向点击的那张；
      // 这样预览里可以左右连着看该卡片的图，而不会串到别的卡片。
      viewerVisible: false,
      viewerList: [],
      viewerIndex: 0,
    };
  },
  // ⚠️ isMobile 必须随视口变化重算：它决定卡 3 是「3 张并排(H5)」还是「单张(PC)」等，
  // 只在 created 时算一次的话，在桌面宽度打开再拖窄到 H5 宽度不会切成 H5 版式
  // （表现为卡 3 只有 1 张图、少 2 张切图）。
  mounted() {
    window.addEventListener("resize", this.handleResize);
    // APICloud（openFrame）下 api 注入可能晚于组件创建：就绪后再判定一次，
    // 否则首帧会误判成 PC（头部不隐藏、且会要求登录）。
    // 同时用 setTimeout 兜底 —— 个别容器不派发 apiready。
    onApiReady(this.syncMobileEnv);
    this.apiFallbackTimer = setTimeout(this.syncMobileEnv, 300);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    if (this.apiFallbackTimer) clearTimeout(this.apiFallbackTimer);
  },
  computed: {
    // H5 隐藏头部导航；PC 保留
    hideHeader() {
      return this.isMobile;
    },
    // 顶部深绿背景带（底部弧线）：两端为不同比例的矢量图
    // 用 <img> + object-fit: fill 而不是 background-image：
    // background 的 size/position 在本页出现过「按固有比例缩放 + 垂直居中」的异常
    // （实测 1024 宽时绿色只占元素中部 105..300，上下漏出底色），object-fit 更确定。
    heroBand() {
      return this.isMobile ? BAND.h5 : BAND.pc;
    },
    // 首屏标题切图
    heroTitle() {
      return this.isMobile ? TITLE_IMG.h5 : TITLE_IMG.pc;
    },
    // 卡片右上角绿色装饰（设计稿独立矢量：PC 160×78 / H5 123×78）。
    // 作为 CSS 变量传给卡片，由 .rd-card::after 承载并自行加 blur（详见样式注释）：
    // 设计稿里这块绿在玻璃**之下**、被 backdrop blur 糊开，直接当 background 会成硬边色块。
    cardCornerStyle() {
      const src = this.isMobile ? CORNER.h5 : CORNER.pc;
      return {
        "--rd-corner": `url("${src}")`,
        "--rd-corner-w": this.isMobile ? "123px" : "160px",
      };
    },
    // PC 单行；H5 设计稿为两行，用 CSS 控制显隐（见 .rd-hero-sub-line）
    heroLines() {
      return this.hero.subtitleLines || [this.hero.subtitle];
    },
  },
  methods: {
    /**
     * 点击卡片配图 → 查看大图（禅道 #23490）
     *
     * 用**事件委托**而不是给每个 <img> 绑 @click：卡片配图有 6 种版式、20 多张图，
     * 且卡 3/7 已是「一张整图含多图」，逐个绑定既啰嗦又容易漏。
     * 这里只认「位于 .rd-media 内」的图片 —— 首屏 hero / 装饰图（在 .rd-hero / .rd-strip 下）
     * 不参与，符合「只给卡片内的图片加」的要求。
     *
     * 点开后可在**同一卡片内**左右切换（卡 4 有 4 张、卡 5 有 8 张、卡 8 有 2 张）。
     * 列表按「当前图片所属的 .rd-media」收集，天然不会串到别的卡片。
     */
    onMainClick(e) {
      const img = e.target && e.target.tagName === "IMG" ? e.target : null;
      if (!img || !img.closest) return;
      // 仅限卡片配图区
      const media = img.closest(".rd-media");
      if (!media) return;
      // 收集同卡片内的全部配图，并定位当前下标
      const imgs = Array.from(media.querySelectorAll("img"));
      if (!imgs.length) return;
      this.viewerList = imgs.map((el) => ({
        src: el.currentSrc || el.src,
        alt: el.alt || "",
      }));
      this.viewerIndex = Math.max(0, imgs.indexOf(img));
      this.viewerVisible = true;
    },
    // 视口跨过 767 断点时切换两端版式（节流，避免拖动过程中频繁重算）
    handleResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.syncMobileEnv, 150);
    },
    // 重新按环境判定端别（APICloud 就绪后 / 视口变化时调用）
    syncMobileEnv() {
      const next = isMobileUtil();
      if (next !== this.isMobile) this.isMobile = next;
    },
    mediaLayout(item) {
      // 版式完全由常量里的 media.layout 决定（卡 3 两端均为 row3，见常量注释）
      return (item.media && item.media.layout) || "phone";
    },
    chunk(list, size) {
      const out = [];
      for (let i = 0; i < list.length; i += size) {
        out.push(list.slice(i, i + size));
      }
      return out;
    },
    goBack() {
      window.history.length > 1 ? this.$router.back() : window.history.back();
    },
  },
};
</script>

<style lang="scss" scoped>
$rd-green-deep: #00452d;
$rd-green: #0fa87b;
$rd-text: #334155;
$rd-title-color: #333;

.rd-main {
  position: relative;
  min-height: 100%;
  padding-bottom: 60px;
  box-sizing: border-box;
  background: #e8f0ed;
  // 首屏区几何：供下方背景带按「内容兜底」计算高度。
  // 改首屏布局（容器顶部留白 / 卡片高度）时只需改这两个变量，背景带自动跟随。
  --rd-hero-top: 116px;
  --rd-hero-h: 326px;
}
// 顶部深绿渐变带要「通栏」到底：滚动容器的经典滚动条会占掉约 15px 宽度，
// 使 100% 的背景带只铺到 (视口 − 滚动条)，右侧漏出一条底色（H5 窄屏尤其明显）。
// 让本页的滚动条改为覆盖式（宽度 0、轨道透明）即可让内容区等于视口宽度。
// 说明：滚轮/触摸/键盘仍可正常滚动，只是不显示常驻滚动条 —— 该页为通栏视觉设计，
// 优先保证背景铺满；如需保留滚动条可去掉下面这段。
.pro-rights-detail ::v-deep .el-main {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* 旧 Edge */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}
// 顶部深绿渐变带：设计稿是「底部带弧线」的矢量（PC 1918×405 / H5 375×229）。
//
// ⚠️ 关键认知：这条 SVG 的**底部左右两角本身就是透明的**。
//   路径 `M0 0 V280.72 C… 1918 280.72 V0 Z`：两端 y 只有 280.72，弧线最低点 404.72。
//   即 viewBox 内 y∈(280.72,405] 的区域，除中间那段弧线外全为透明 —— 拉伸后就是
//   「左右下角漏出页面底色」。这是图本身的形状（设计稿里那块就该露出浅色底），
//   不是加载方式的问题（background 与 <img> 都一样）。
//   实测确认：把图 drawImage 到 canvas 打品红底，bottomLeft/bottomRight 均为品红。
//
// 因此高度不能随便拉伸去「铺满」——若带高远小于弧线需要，弧线会被压扁；
// 若强行拉高，两角的透明区会更大。正确语义是：
//   带高 = 弧线最低点距页顶的距离 = «首屏卡顶部留白 + 卡片高 × 88.6%»
//   其中 88.6% 由设计稿反推：(404.72 − 116) / 326；H5 为 (204.63 − 103) / 156 ≈ 65%。
//   这样弧线最低点恰好落在设计稿的相对位置，两角留出的浅色三角也与设计稿一致。
//
// 自适应：只需改 --rd-hero-top / --rd-hero-h / --rd-band-overlap 三个变量，
// 断点切换（PC / 平板 / H5）时自动跟随；不再写死 405 / 229。
//
// ⚠️ 用 <img> + object-fit: cover，不用 background-image、也不用 fill：
//   · background：本页出现过「按固有比例缩放 + 垂直居中」（1024 宽时绿色只占元素中部
//     y105..300），尺寸/定位不可控；
//   · fill：把图**分别按宽高拉伸** —— 元素比例（1024/405≈2.53）与图比例（1918/405≈4.74）
//     不一致时，纵向被拉伸 1.87 倍，弧线两侧的透明区（见上方说明）随之等比放大，
//     视觉上就是「上下没铺满」；
//   · cover：按「铺满元素」的等比缩放（取宽高比中的大者），超出部分裁切。等比例不畸变，
//     且裁切会把弧线两端透明最厚的部分切掉，露底面积显著减小。
//
// object-position 用 **`center bottom`（50% 100%）**，而不是默认的 center center：
// 宽视口下封面会转为「按宽度缩放」（元素比图更宽时），此时图比元素高、需要纵向裁切。
// center center 会上下各裁一半，把**弧线最低点切掉**（2560 宽实测上下各裁 ~68px）；
// 改为 bottom 后图底边与元素底边对齐，纵向裁切**全部落在顶部**——
// 顶部是平直的纯绿色，裁掉无影响；弧线最低点（图中 99.9% 处）则始终完整露出。
// 窄视口（按高度缩放）本就没有纵向裁切，bottom 不产生副作用。
//
// 说明：弧线两侧的透明三角是**图形本身**（见上方路径分析），cover 只能缩小、不能完全消除；
// 若要做到任何分辨率都完全没有浅色露出，需要改 SVG 路径（把绿色补到画布左右下角），
// 代价是弧线两端被"填平"、形状与设计稿不再一致。
.rd-hero-bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: calc(
    var(--rd-hero-top) + var(--rd-hero-h) * var(--rd-band-overlap, 0.886)
  );
  object-fit: cover;
  object-position: center bottom;
  display: block;
  pointer-events: none;
  user-select: none;
}
.rd-container {
  position: relative;
  margin: 0 auto;
  padding-top: 116px;
  width: 1098px;
  display: flex;
  flex-direction: column;
  gap: 59px;
}
// 首屏玻璃卡
.rd-hero {
  position: relative;
  height: 326px;
  padding: 73px 50px 0;
  box-sizing: border-box;
  border-radius: 35px;
  // 玻璃质感三层（设计稿 13776:836 / 13776:837）：
  //  1. 半透明白底 rgba(255,255,255,.1) + 左上柔光（同 Mask group 的模糊高光）
  //  2. backdrop-filter 模糊身后的深绿渐变带 —— 这是「毛玻璃」的关键，
  //     缺了它只剩一层平的白色蒙版（此前实现漏了此条，故观感很“假”）
  //  3. 底部渐隐的渐变描边（见 ::before）——用 border 会得到实心边，底部留硬线
  // 注意：不能加 overflow: hidden，设计稿的主视觉是**溢出**卡片的（y=-10 高 355）
  background: radial-gradient(
      620px 340px at 6% -12%,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0) 100%
    ),
    rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(23.05px);
  -webkit-backdrop-filter: blur(23.05px);
  box-shadow: 0 17px 17px 0 rgba(15, 168, 123, 0.1);
  // 渐变描边：用 mask 抠掉 padding-box，只留 1.74px 边框环，
  // 自上而下由 rgba(255,255,255,.19) 渐隐到透明 → 底部无硬边
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1.74px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.19) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }
}
// 标题为设计稿切图（2x 导出）：必须显式折算显示尺寸，
// 否则 <img> 会按原始像素渲染（PC 1082px 宽 / H5 340px 宽）而撑破卡片。
// 设计稿实际尺寸：PC 543×60，H5 170×36
.rd-hero-title {
  display: block;
  margin: 0;
  width: 543px;
  max-width: 100%;
  height: auto;
}
// 卡片内的 V 徽章水印：设计稿节点 2374:297 位于卡片内 (70, 91)，尺寸 222×191
//（卡片 1098×326，原点 1107,6945 → 1177,7036）。
// SVG 自身带 fill-opacity: .05，此处不再调透明度，避免叠加后过淡。
//
// z-index 必须为**负**：`.rd-hero-text` 是无定位的普通块，按绘制顺序属于
// 「in-flow 非定位后代」层，而 z-index:0/auto 的定位元素会**盖在它上面**。
// 负值才能落到文字之下、卡片背景之上（`.rd-hero` 有 backdrop-filter，
// 已形成层叠上下文，负值不会穿到卡片外）
.rd-hero-mark {
  position: absolute;
  left: 70px;
  top: 91px;
  width: 222px;
  height: 191px;
  pointer-events: none;
  z-index: -1;
}
.rd-hero-sub {
  margin: 35px 0 0;
  font-size: 24px;
  line-height: 35px;
  color: #e8e0ac;
}
// PC 显示单行版，隐藏两行版（两者内容一致，仅断行不同）
.rd-hero-sub--h5 {
  display: none;
}
.rd-hero-sub-line {
  display: inline;
}
.rd-hero-visual {
  position: absolute;
  right: 50px;
  top: -10px;
  width: 413px;
  height: 355px;
  object-fit: contain;
}
// "9 项专业版核心权益解释"
.rd-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
}
.rd-strip-deco {
  width: 38px;
  height: 40px;
  flex-shrink: 0;
}
.rd-strip-text {
  display: inline-flex;
  align-items: baseline;
  gap: 14px;
}
.rd-strip-num {
  font-family: "DIN Alternate", "Arial Black", sans-serif;
  font-weight: 700;
  font-size: 96px;
  line-height: 0.9;
  color: $rd-green;
}
.rd-strip-desc {
  font-size: 32px;
  color: $rd-title-color;
}
// 卡片区
.rd-benefits {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}
.rd-card {
  position: relative;
  flex: 1 1 100%;
  padding: 18px 24px 24px;
  box-sizing: border-box;
  border-radius: 35px;
  // 纵向 flex：让配图区可用 margin-top:auto 贴到卡片底部。
  // 卡 1/2 并排等高（.rd-benefits 的 align-items 默认 stretch），
  // 但两者文案长短不同 —— 配图底部对齐后两张手机截图才会落在同一水平线上
  display: flex;
  flex-direction: column;
  // 玻璃卡外观：白色半透明渐变 + backdrop 模糊 + 底部渐隐渐变描边 + 轻阴影
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.82) 2%,
    rgba(255, 255, 255, 0.6) 60%
  );
  backdrop-filter: blur(23.05px);
  -webkit-backdrop-filter: blur(23.05px);
  box-shadow: 0 4px 10px 0 rgba(13, 175, 122, 0.1);
  // 裁切内部晕染，避免模糊外溢到卡片外
  overflow: hidden;
  // 渐变描边（设计稿 Union 的 stroke：白 → 透明，约 65% 处淡出）：
  // 用 border 是实心的，底部会有一条硬线，与卡片「渐隐收边」的观感不符
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0) 65%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
    z-index: 2;
  }
  // 右上角绿色装饰（设计稿 PC 160×78 / H5 123×78）。
  // ⚠️ 设计稿里这块绿色压在玻璃**之下**：先是一层 backdrop blur(23.05px) 把它糊开，
  // 再叠白色 0.82→0.6 渐变把它冲淡 —— 所以观感是一团边界模糊的绿色晕染。
  // 若把切图直接当 background 叠在卡片上，就是一块边界锐利的色块（此前实现即此问题，
  // 因为背景图不会被父级 backdrop-filter 模糊）。故单独用 ::after 承载并自带 blur。
  &::after {
    content: "";
    position: absolute;
    top: 4px;
    right: 4px;
    width: var(--rd-corner-w, 160px);
    height: 78px;
    border-radius: 20px;
    background-image: var(--rd-corner);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100% 100%;
    filter: blur(22px);
    // 透明度按设计稿实测校准：设计稿该处峰值「绿色浓度」(G-(R+B)/2) ≈ 26、rgb(207,244,230)，
    // 而 0.55 时实测 62（约 2.4 倍过深）。逐档实测：0.22→28（最接近）、0.3→36、0.35→40。
    opacity: 0.22;
    pointer-events: none;
    z-index: 0;
  }
}
// 卡 1/2 在 PC 上并排（各占一半）
.rd-card--half {
  flex: 1 1 calc(50% - 15px);
}
.rd-card-title {
  position: relative;
  // 抬起层级：卡片 ::after 的绿色晕染是最后一个子节点，同层位下会盖住文字
  z-index: 1;
  margin: 0 0 16px;
  font-size: 24px;
  font-weight: 700;
  line-height: 22px;
  color: $rd-title-color;
}
.rd-card-body {
  position: relative;
  z-index: 1;
  margin-bottom: 20px;
}
.rd-para {
  margin: 0 0 10px;
  font-size: 16px;
  line-height: 24px;
  color: $rd-text;
  &:last-child {
    margin-bottom: 0;
  }
}
.rd-list {
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    // 设计稿：项目符号圆点距列表左沿 13px、文字距 27px
    // （卡片左沿 410 → 正文块 468 → 圆点 481 / 文字 495）
    padding-left: 27px;
    margin-bottom: 10px;
    font-size: 16px;
    line-height: 24px;
    color: #666;
    // 圆点用 CSS 画（设计稿实测 4×4、#333）：
    // 原实现是 content:"-" 的横线，与设计稿不符（禅道 #23492）
    &::before {
      content: "";
      position: absolute;
      left: 13px;
      top: 10px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #333;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
}
// 重点片段：加粗 + 绿色（设计稿 ts1 / ts2）
.is-strong {
  font-weight: 700;
  color: #0abc7b;
}
// 物联网类别名：加粗深色（设计稿 ts3）
// display:block 让类别名**独占一行**，说明文字另起一行（禅道 #23492）。
// PC 与 H5 设计稿在这一点上一致（均为「圆点 + 类别名 / 说明」两行结构）。
.is-label {
  display: block;
  font-weight: 700;
  color: $rd-title-color;
}
// 配图区
.rd-media {
  position: relative;
  z-index: 1;
  // 配图区贴卡片底部：卡 1/2 并排等高，文案长短不同时靠此把图片压到底部对齐，
  // 两张手机截图才会在同一水平线上（此前文案长的卡片把图往下顶，两卡错位）
  margin-top: auto;
  // 图片整体水平居中（设计稿中单图/多图都是居中排布）
  display: flex;
  justify-content: center;
  // 兜底：截图原图分辨率很高（如 1260px 宽），未显式设定宽度时会按原尺寸撑破卡片
  // （曾导致卡 9 被撑到 2589px）。此处统一限制，各 layout 再各自覆盖具体宽度。
  img {
    max-width: 100%;
  }
  // 点击可看大图（见 onMainClick）：给配图加手型光标与轻微反馈
  img {
    cursor: zoom-in;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.88;
    }
  }
}
// 多图并排的几种 layout 需要撑满可用宽度（否则 flex 居中会让它们缩成内容宽）
.rd-media--row4,
.rd-media--row3,
.rd-media--duo,
.rd-media--wide,
.rd-media--mapPair {
  display: block;
}
.rd-phone {
  position: relative;
  width: 204px;
  border-radius: 17px;
  overflow: hidden;
  // 单张手机截图：水平居中（.rd-media 已是 flex + center，此处兜底宽度）
  flex: 0 0 auto;
  img {
    display: block;
    width: 100%;
  }
}
// 卡 3：3 张手机截图并排，**3 张独立切图**（橙色说明已烧进第 1 张图内）。
// 圆角统一加在「组」容器上（设计稿组 598×396、圆角 24；组内单图无圆角），
// 切图本身是完整矩形 —— 这样预览时看到的是完整图片，不会留下切图带来的白角。
.rd-row3 {
  display: flex;
  // 组内几乎无间隙（设计稿约 1px），3 张拼成一条连续画面
  gap: 1px;
  max-width: 598px;
  margin: 0 auto;
  border-radius: 24px;
  overflow: hidden;
}
.rd-row3-item {
  flex: 1 1 0;
  min-width: 0;
  img {
    display: block;
    width: 100%;
  }
}
// 卡 4 / 卡 5 的并排图（禅道 #23492）：
// 设计稿里正文块与**配图区**左右各内缩 34px（标题 +24、内容 +58，内容宽 982 = 1050 − 34×2）；
// 例外是卡 1/2（两列 phone 版式）——它们的内容与标题齐平。
// 用 margin 而非 padding：[左右各缩 34] 正好复现设计稿的 982 宽，折行位置也一致。
.rd-card:not(.rd-card--half) {
  .rd-para,
  .rd-list,
  // 配图区与正文同宽（设计稿里两者同为 x=58 起、982 宽）。
  // 原先只缩了正文、漏了配图 → 卡 6/8 的图比设计稿宽 68px、边距偏小
  .rd-media {
    margin-left: 34px;
    margin-right: 34px;
  }
}
// 「组」容器：卡 4 一组 → 827 居中；卡 5 两组 → 984，组间 30px（设计稿 476 + 30 + 474）
.rd-row4-wrap {
  display: flex;
  align-items: flex-start; // 两组各自高度（设计稿 235 / 239），互不拉伸
  gap: 30px;
  justify-content: center;
  max-width: 827px;
  margin: 0 auto;
  &.is-multi {
    max-width: 984px;
  }
}
.rd-row4 {
  display: flex;
  // 组内几乎无间隙（设计稿约 1px），4 张拼成一条连续画面
  gap: 1px;
  flex: 1 1 0;
  min-width: 0;
  // 圆角加在**组**上、配合 overflow:hidden，让四张拼成的整块只有外圈带圆角
  // （设计稿：组有圆角、组内单图无圆角）。卡 4 组 12px（禅道 #23492）
  border-radius: 12px;
  overflow: hidden;
  // 组高度由设计稿定（卡 4：406/205 宽比；卡 5：235/119）
  // 必须显式给高度：4 张原图比例略有差异，若各自按内容撑高，底边会参差不齐、
  // 且 object-fit: cover 在没有确定高度时形同虚设（起不到裁切对齐作用）
  height: var(--rd-row4-h, 409px);
  img {
    display: block;
    // 用 flex 平分剩余空间（而非 width:25%）：4 张图 + 3 个 gap 时
    // width:25% 会把总宽撑出容器 3×gap（曾溢出 12px），flex 则自动扣除 gap
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    object-fit: cover;
  }
}
// 卡 5：两组并排，设计稿圆角为 18.71px（与卡 4 的 12px 不同）
.rd-row4-wrap.is-multi .rd-row4 {
  border-radius: 18.71px;
  // 卡 5 每组高度 235/119 宽比 → 476 宽时约 235px
  height: 235px;
}
.rd-duo-item {
  position: relative;
  // 卡 7/9 设计稿：每张图各自 12px 圆角（组本身 21.58px 但组内图片是独立圆角卡片，
  // 与卡 4/5 的「整组一块」不同）
  border-radius: 12px;
  overflow: hidden;
  img {
    display: block;
    width: 100%;
  }
}
.rd-media--duo {
  display: flex;
  // 卡 7（溯源页对比）/ 卡 9（区块链）：两张独立图各 200 宽
  //（设计稿：内容区 982，两张共占约 43%），居中；组间 19px
  gap: 19px;
  justify-content: center;
  > div {
    flex: 0 1 200px;
    min-width: 0;
  }
  img {
    display: block;
    width: 100%;
  }
}
.rd-wide {
  border-radius: 34px;
  overflow: hidden;
  // 卡 6 设计稿：图 902×408，在 982 宽内容区里**居中**（左右各留 40），并非铺满
  max-width: 902px;
  margin-left: auto;
  margin-right: auto;
  // 整张切图，红色标注已烧进图内；圆角由本容器统一裁
  img {
    display: block;
    width: 100%;
  }
}
// 卡 8：PC 设计稿为**一行两图**（禅道 #23492）：
//   8-1 导出弹窗对比 617x239 —— 8-2 俯瞰图对比 364.82x270
// 合计 982 = 内容区宽，组间 0，垂直居中（两组容器中心线同在 y=4933）。
// 两张均为「整组切图」，红色说明文字与角标都已烧进图内，页面不做 DOM 叠加。
.rd-maprow {
  display: flex;
  align-items: center;
}
// 8-1：两个弹窗对比（含红色说明）
.rd-dialogs {
  flex: 617 1 0;
  min-width: 0;
  border-radius: 23px;
  overflow: hidden;
  img {
    display: block;
    width: 100%;
  }
}
// 8-2：俯瞰图对比（含「专业版 / 免费版」角标）
.rd-dialog {
  position: relative;
  flex: 365 1 0;
  min-width: 0;
  border-radius: 17px;
  overflow: hidden;
  img {
    display: block;
    width: 100%;
  }
}

/* ==================== 窄屏 PC / 平板（容器 1098 放不下时）====================
   设计稿只给了 PC（1918）与 H5（375）两档；768~1439 这一带若沿用 1098 固定宽
   会横向溢出，故让容器改为「随视口收缩、左右留 16px」。 */
@media (max-width: 1130px) and (min-width: 768px) {
  // 该档首屏卡为 auto 高（min 240），背景带随之推导；
  // 顶部留白沿用 .rd-container 的 padding-top（此档未覆盖，仍是 116px）
  .rd-main {
    --rd-hero-h: 240px;
  }
  .rd-container {
    width: auto;
    padding-left: 16px;
    padding-right: 16px;
  }
  .rd-hero {
    height: auto;
    min-height: 240px;
    padding: 40px 32px 32px;
    border-radius: 24px;
  }
  .rd-hero-title {
    font-size: 42px;
    line-height: 46px;
  }
  .rd-hero-sub {
    margin-top: 18px;
    font-size: 18px;
    line-height: 28px;
  }
  .rd-hero-visual {
    width: 280px;
    height: 240px;
    right: 24px;
    top: 0;
  }
  .rd-strip-num {
    font-size: 64px;
  }
  .rd-strip-desc {
    font-size: 24px;
  }
  .rd-card {
    border-radius: 24px;
  }
}

/* ============================ H5（移动端）============================ */
@media (max-width: 767px) {
  .rd-main {
    padding-bottom: 40px;
    // H5 首屏区几何（供背景带推导高度；与下方 .rd-container / .rd-hero 取值保持一致）。
    // 设计稿实测（节点 13664:1128 帧，绝对坐标）：
    //   状态栏 44 + 导航栏 42 + 间距 22 → 卡片顶 = 110；卡片高 138；弧线最低点 229。
    // H5 页内**不渲染**状态栏与导航栏（页面自带 hide-header，且 APICloud 用 openframe
    // 打开时宿主已有标题栏），故只保留设计稿里「导航栏→卡片」的那 22px 呼吸间距。
    --rd-hero-top: 22px;
    --rd-hero-h: 138px;
    // 弧线最低点相对卡片顶：229 − 110 = 119 → 119 / 138 ≈ 86.2%
    //（这样弧线与卡片的相对关系与设计稿完全一致：弧线在卡片底边上方 19px）
    --rd-band-overlap: 0.862;
  }
  // 背景带高度由上面的变量推导（见 .rd-hero-bg 基础规则），此处只需换图
  .rd-container {
    width: auto;
    // 顶部留白 22px = 设计稿「导航栏 → 卡片」的间距（13666:1215 顶 − 导航栏底）。
    // 原先取 103 是把状态栏(44)+导航栏(42) 也算进去了 —— H5 页内两者都不渲染，
    // 那段绿带全是空白，看起来「头部留得太高」。
    padding: 22px 16px 0;
    gap: 16px;
  }
  .rd-hero {
    // 卡片可见矩形：设计稿 13666:1203 = 343×138（圆角 20）
    height: 138px;
    padding: 19px 0 0 24px;
    border-radius: 20px;
  }
  // 首屏 V 徽章水印（H5 版）：设计稿节点 13666:1227，105×90 @ 卡内 (29, 18)
  // 注：PC 版是 222×191 @ (70, 91) —— 两端尺寸/位置都不同，各自覆盖
  .rd-hero-mark {
    left: 29px;
    top: 18px;
    width: 105px;
    height: 90px;
  }
  .rd-hero-title {
    // H5 标题切图设计尺寸 170×36
    width: 170px;
  }
  .rd-hero-sub {
    // 设计稿：标题底 13498 → 副标题顶 13507，间距 9px
    margin-top: 9px;
    font-size: 12px;
    line-height: 20px;
  }
  // H5 显示两行版，隐藏 PC 单行版
  .rd-hero-sub--pc {
    display: none;
  }
  .rd-hero-sub--h5 {
    display: block;
    .rd-hero-sub-line {
      display: block;
    }
  }
  .rd-hero-visual {
    // 设计稿 13666:1217：146×126 @ 卡内 (188, 6) → 右间距 = 343 − 188 − 146 = 9
    right: 9px;
    top: 6px;
    width: 146px;
    height: 126px;
  }
  .rd-strip {
    gap: 12px;
  }
  .rd-strip-deco {
    width: 21px;
    height: 23px;
  }
  .rd-strip-text {
    gap: 6px;
  }
  .rd-strip-num {
    font-size: 48px;
  }
  .rd-strip-desc {
    font-size: 16px;
  }
  .rd-benefits {
    gap: 16px;
  }
  .rd-card {
    padding: 15px 16px 16px;
    border-radius: 20px;
    // H5 一律纵向堆叠（含卡 1、2）
    &--half {
      flex: 1 1 100%;
    }
  }
  .rd-card-title {
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 22px;
  }
  .rd-para,
  .rd-list li {
    font-size: 14px;
    line-height: 22px;
  }
  .rd-phone {
    width: 150px;
    border-radius: 12px;
  }
  // 卡 3：H5 内容区仅约 328 宽，去掉 598 上限自然铺满
  .rd-row3 {
    max-width: none;
  }
  // 正文内缩与配图分组均为 PC 设计稿要求，H5 保持原样：
  // 取消 34px 内缩（正文与配图都要还原，两者在 PC 上是同一套内缩规则）、
  // 两组改回上下两行（H5 宽度放不下 8 张一行）
  .rd-card:not(.rd-card--half) {
    .rd-para,
    .rd-list,
    .rd-media {
      margin-left: 0;
      margin-right: 0;
    }
  }
  .rd-row4-wrap {
    display: block;
    max-width: none;
  }
  // 卡 4 / 卡 5 的配图（H5 设计稿实测）：
  //   卡 4「5」     = 306×151，4 张各 76×151（**原图比例**，张与张紧贴无间隙）
  //   卡 5「5-1/5-2」= 305×151 / 304×153，两组**上下排列**、组间距 10px，圆角均 12
  // 关键：核心尺寸按图自适应，不再沿用 PC 的固定高度。
  //   PC 那套 `height: 409/235px` + `object-fit: cover` 是为「PC 组更宽、需裁切对齐底边」
  //   而设；H5 组内每张仅 ~76 宽，若沿用会被压成 75×409
  //   （比例 0.18 vs 原图 0.50，横向压扁近 3 倍）—— 这就是「图片拉伸太厉害」的根因。
  .rd-row4 {
    max-width: none;
    // 设计稿张与张之间无间隙（76+76+77+77 = 306 = 组宽）
    gap: 0;
    // 两组间距 10px（5-1 底 16590 → 5-2 顶 16600）
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }
    // 高度交给图片自然撑开（各图比例几乎一致，差值 ≤2px，由 overflow:hidden 兜住）
    height: auto;
    img {
      height: auto;
      // 不再裁切：H5 是完整展示每张截图，而不是像 PC 那样裁成统一高度
      object-fit: fill;
    }
  }
  // 卡 5 两组：H5 设计稿圆角为 12（18.71 是 PC 稿的取值），高度同样按图自适应。
  // 选择器需与基础规则同权重（`.rd-row4-wrap.is-multi .rd-row4`）才能覆盖，
  // 只写 `.rd-row4` 会因权重低而失效
  .rd-row4-wrap.is-multi .rd-row4 {
    border-radius: 12px;
    height: auto;
  }
  .rd-wide {
    border-radius: 12px;
    // 解除 PC 的 902 限宽：H5 内容区本就窄，需铺满
    max-width: none;
  }
  // 卡 9：基础规则为 PC 的 200 固定宽；H5 两张图应平分容器铺满，故此处解除上限
  .rd-media--duo > div {
    flex: 1 1 0;
  }
  // 卡 8：基础规则为 PC 的「一行两图」；H5 为**上下两行**
  //（8-1 弹窗对比一行、8-2 俯瞰图对比一行，两张均铺满）
  .rd-maprow {
    display: block;
  }
  .rd-dialogs {
    border-radius: 12px;
  }
  .rd-dialog {
    width: 100%;
    margin-top: 8px;
  }
}
</style>
